import React, { useEffect, useState } from "react";
import { api, formatApiError, rupiah, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import QrScanner from "@/components/QrScanner";
import { Fingerprint, Plus, Minus, Trash2, ShoppingCart, Search, Wallet, CheckCircle2, ScanLine, QrCode, GraduationCap, BookUser, User, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function POS() {
  const { user } = useAuth();
  const isCashier = user?.role === "cashier";
  const [mode, setMode] = useState("santri"); // "santri" | "pendidik"
  const [unit, setUnit] = useState(isCashier ? user?.unit || "putra" : "putra");
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [pin, setPin] = useState("");
  const [student, setStudent] = useState(null);
  const [educator, setEducator] = useState(null);
  const [ascCard, setAscCard] = useState(null);
  const [cart, setCart] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [scanFor, setScanFor] = useState(null); // "student" | "product"

  const loadProducts = (u) => api.get(`/cashier/products?unit=${u}`).then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { loadProducts(unit); resetBuyer(); setCart([]); }, [unit]);

  const resetBuyer = () => { setStudent(null); setEducator(null); setAscCard(null); setPin(""); };
  const switchMode = (m) => { if (m === mode) return; setMode(m); resetBuyer(); setCart([]); };

  const doLookup = async (kind, value) => {
    if (!value || !String(value).trim()) return;
    const path = kind === "code" ? "/cashier/lookup-code" : "/cashier/lookup";
    const body = kind === "code" ? { code: String(value).trim(), unit } : { pin: String(value).trim(), unit };
    try {
      const { data } = await api.post(path, body);
      setStudent(data);
      toast.success(`Santri: ${data.name}`);
    } catch (err) {
      setStudent(null);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const lookupEducator = async (value) => {
    if (!value || !String(value).trim()) return;
    try {
      const { data } = await api.post("/cashier/lookup-educator", { pin: String(value).trim() });
      setEducator(data);
      toast.success(`Pendidik: ${data.name}`);
    } catch (err) {
      setEducator(null);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const lookupAsc = async (value) => {
    if (!value || !String(value).trim()) return;
    try {
      const { data } = await api.post("/cashier/asc-lookup", { card_number: String(value).trim() });
      setAscCard(data);
      toast.success(`Kartu ASC ${data.card_number}`);
    } catch (err) {
      setAscCard(null);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const lookup = (e) => {
    e.preventDefault();
    if (mode === "santri") doLookup("pin", pin);
    else if (mode === "pendidik") lookupEducator(pin);
    else lookupAsc(pin);
  };

  const addToCart = (p) => {
    if (p.stock <= 0) return toast.error("Stok habis");
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) {
        if (ex.qty >= p.stock) { toast.error("Melebihi stok"); return c; }
        return c.map((i) => (i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...c, { product_id: p.id, name: p.name, price: p.price, qty: 1, stock: p.stock }];
    });
  };

  const onScanProduct = (text) => {
    setScanFor(null);
    const code = String(text).trim().toLowerCase();
    const p = products.find((x) => (x.sku || "").toLowerCase() === code || (x.id || "").toLowerCase() === code);
    if (p) { addToCart(p); toast.success(`Ditambahkan: ${p.name}`); }
    else toast.error(`Barang dengan kode "${text}" tidak ditemukan di unit ${unit}`);
  };

  const changeQty = (pid, d) =>
    setCart((c) => c.map((i) => (i.product_id === pid ? { ...i, qty: Math.max(1, Math.min(i.stock, i.qty + d)) } : i)));
  const removeItem = (pid) => setCart((c) => c.filter((i) => i.product_id !== pid));

  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const insufficient = (mode === "santri" && student && total > student.personal_balance)
    || (mode === "asc" && ascCard && total > ascCard.balance);

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Keranjang kosong");
    setProcessing(true);
    try {
      if (mode === "santri") {
        if (!student) { setProcessing(false); return toast.error("Pindai PIN/QR santri dulu"); }
        const { data } = await api.post("/cashier/sale", {
          student_id: student.id, unit,
          items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        });
        toast.success(`Transaksi berhasil! Sisa uang jajan ${rupiah(data.remaining_balance)}`);
      } else if (mode === "pendidik") {
        if (!educator) { setProcessing(false); return toast.error("Masukkan PIN pendidik dulu"); }
        const { data } = await api.post("/cashier/educator-sale", {
          educator_id: educator.id,
          items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        });
        toast.success(`Belanja ${educator.name} dicatat (${rupiah(data.total)}) — ditagih saat gajian`);
      } else {
        if (!ascCard) { setProcessing(false); return toast.error("Pindai / masukkan nomor kartu ASC dulu"); }
        const { data } = await api.post("/cashier/asc-sale", {
          card_number: ascCard.card_number, unit,
          items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        });
        toast.success(`Transaksi berhasil! Sisa saldo kartu ${rupiah(data.remaining_balance)}`);
      }
      setCart([]); resetBuyer(); loadProducts(unit);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProcessing(false);
    }
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));
  const buyerReady = mode === "santri" ? !!student : mode === "pendidik" ? !!educator : !!ascCard;

  return (
    <div>
      <PageHeader
        title="Penjualan Kopontren"
        subtitle={mode === "santri"
          ? "Pindai PIN atau QR santri, lalu proses belanja. Uang jajan dipotong otomatis."
          : mode === "pendidik"
          ? "Masukkan PIN pendidik. Belanja dicatat sebagai utang, tanpa potong saldo, bebas lintas unit."
          : "Pindai QR atau masukkan nomor Kartu ASC, lalu proses belanja. Saldo kartu dipotong otomatis."}
        action={
          isCashier ? (
            <span data-testid="pos-unit-label" className={`px-4 py-2 rounded-xl text-sm font-semibold ${unit === "putra" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>
              Unit {unit === "putra" ? "Putra" : "Putri"}
            </span>
          ) : (
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger data-testid="pos-unit-select" className="w-44 rounded-xl bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="putra">Kopontren Putra</SelectItem>
                <SelectItem value="putri">Kopontren Putri</SelectItem>
              </SelectContent>
            </Select>
          )
        }
      />

      {/* Mode toggle */}
      <div className="inline-flex p-1 mb-5 bg-stone-100 rounded-2xl border border-stone-200">
        <button onClick={() => switchMode("santri")} data-testid="pos-mode-santri"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${mode === "santri" ? "bg-white text-emerald-700 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}>
          <GraduationCap size={17} /> Santri
        </button>
        <button onClick={() => switchMode("pendidik")} data-testid="pos-mode-pendidik"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${mode === "pendidik" ? "bg-white text-emerald-700 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}>
          <BookUser size={17} /> Pendidik
        </button>
        <button onClick={() => switchMode("asc")} data-testid="pos-mode-asc"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${mode === "asc" ? "bg-white text-emerald-700 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}>
          <CreditCard size={17} /> ASC
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="pos-search" placeholder="Cari barang…" className="pl-10 rounded-xl py-6 bg-white" />
            </div>
            <Button type="button" onClick={() => setScanFor("product")} data-testid="pos-scan-product" variant="outline" className="rounded-xl py-6 px-4">
              <ScanLine size={18} className="mr-1.5" /> Scan
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => addToCart(p)} data-testid={`product-${p.id}`} disabled={p.stock <= 0}
                className="text-left bg-white border border-stone-200 rounded-2xl p-4 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all disabled:opacity-40 active:scale-[0.98]">
                <p className="font-medium text-stone-800 text-sm leading-tight line-clamp-2">{p.name}</p>
                <p className="font-heading font-bold text-emerald-600 mt-2">{rupiah(p.price)}</p>
                <p className="text-[11px] text-stone-400 mt-1">Stok: {p.stock} · {p.category}</p>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-stone-400 text-sm col-span-full py-8 text-center">Tidak ada barang.</p>}
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-5 h-fit lg:sticky lg:top-6">
          <form onSubmit={lookup} className="flex gap-2 mb-2">
            <div className="relative flex-1">
              <Fingerprint size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-500" />
              <Input value={pin} onChange={(e) => setPin(e.target.value)} data-testid="pos-pin-input"
                placeholder={mode === "santri" ? "PIN santri (sidik jari)" : mode === "pendidik" ? "PIN pendidik" : "Nomor kartu ASC"} className="pl-10 rounded-xl" />
            </div>
            <Button type="submit" data-testid="pos-lookup-button" className="bg-stone-900 hover:bg-stone-800 rounded-xl">Cek</Button>
          </form>
          {mode === "santri" && (
            <Button type="button" onClick={() => setScanFor("student")} data-testid="pos-scan-student" variant="outline" className="w-full rounded-xl mb-4">
              <QrCode size={16} className="mr-1.5" /> Pindai QR Santri
            </Button>
          )}
          {mode === "pendidik" && (
            <Button type="button" onClick={() => setScanFor("educator")} data-testid="pos-scan-educator" variant="outline" className="w-full rounded-xl mb-4">
              <QrCode size={16} className="mr-1.5" /> Pindai QR Pendidik
            </Button>
          )}
          {mode === "asc" && (
            <Button type="button" onClick={() => setScanFor("asc")} data-testid="pos-scan-asc" variant="outline" className="w-full rounded-xl mb-4">
              <QrCode size={16} className="mr-1.5" /> Pindai QR Kartu ASC
            </Button>
          )}

          {mode === "santri" ? (
            student ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 mb-4 flex gap-3" data-testid="pos-student-info">
                <BuyerPhoto path={student.photo_path} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                    <p className="font-medium text-stone-800 truncate">{student.name}</p>
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">NISN: {student.nisn || "-"}</p>
                  <p className="text-sm text-stone-500 flex items-center gap-1 mt-1"><Wallet size={14} /> Uang Jajan: <b className="text-emerald-700">{rupiah(student.personal_balance)}</b></p>
                </div>
              </div>
            ) : (
              <div className="rounded-xl bg-stone-50 border p-3 mb-4 text-sm text-stone-400 text-center">Pindai PIN / QR santri</div>
            )
          ) : mode === "pendidik" ? (
            educator ? (
            <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 mb-4 flex gap-3" data-testid="pos-educator-info">
              <BuyerPhoto path={educator.photo_path} />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-sky-600 shrink-0" />
                  <p className="font-medium text-stone-800 truncate">{educator.name}</p>
                </div>
                <p className="text-xs text-stone-500 mt-0.5">NIK: {educator.nik || "-"}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${educator.category === "kependidikan" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {educator.category === "kependidikan" ? "Kependidikan" : "Pendidik"}
                  </span>
                  {educator.jabatan && <span className="text-xs text-stone-500 truncate">{educator.jabatan}</span>}
                </div>
              </div>
            </div>
            ) : (
              <div className="rounded-xl bg-stone-50 border p-3 mb-4 text-sm text-stone-400 text-center">Masukkan PIN pendidik</div>
            )
          ) : ascCard ? (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-4 flex gap-3" data-testid="pos-asc-info">
              <div className="w-14 h-14 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0"><CreditCard size={22} className="text-amber-600" /></div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-amber-600 shrink-0" />
                  <p className="font-medium text-stone-800 truncate">Kartu ASC</p>
                </div>
                <p className="text-xs text-stone-500 mt-0.5 font-mono">{ascCard.card_number}</p>
                <p className="text-sm text-stone-500 flex items-center gap-1 mt-1"><Wallet size={14} /> Saldo: <b className="text-amber-700">{rupiah(ascCard.balance)}</b></p>
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-stone-50 border p-3 mb-4 text-sm text-stone-400 text-center">Pindai / masukkan nomor kartu ASC</div>
          )}

          <div className="flex items-center gap-2 mb-3 text-stone-700">
            <ShoppingCart size={18} /> <p className="font-heading font-semibold">Keranjang</p>
            <span className="ml-auto text-xs bg-stone-100 px-2 py-0.5 rounded-full">{cart.length}</span>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {cart.length === 0 ? (
              <p className="text-stone-400 text-sm py-4 text-center">Keranjang kosong</p>
            ) : (
              cart.map((i) => (
                <div key={i.product_id} className="flex items-center gap-2" data-testid={`cart-item-${i.product_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">{i.name}</p>
                    <p className="text-xs text-stone-400">{rupiah(i.price)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => changeQty(i.product_id, -1)} className="w-6 h-6 rounded-md bg-stone-100 flex items-center justify-center hover:bg-stone-200"><Minus size={12} /></button>
                    <span className="w-6 text-center text-sm font-medium">{i.qty}</span>
                    <button onClick={() => changeQty(i.product_id, 1)} className="w-6 h-6 rounded-md bg-stone-100 flex items-center justify-center hover:bg-stone-200"><Plus size={12} /></button>
                    <button onClick={() => removeItem(i.product_id)} className="w-6 h-6 rounded-md text-rose-500 flex items-center justify-center hover:bg-rose-50"><Trash2 size={12} /></button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t mt-4 pt-4">
            <div className="flex justify-between items-center mb-3">
              <span className="text-stone-500">Total</span>
              <span className="font-heading text-2xl font-bold text-stone-900" data-testid="pos-total">{rupiah(total)}</span>
            </div>
            {insufficient && <p className="text-xs text-rose-600 mb-2 text-center">{mode === "asc" ? "Saldo kartu ASC tidak cukup" : "Uang jajan santri tidak cukup"}</p>}
            {mode === "pendidik" && educator && cart.length > 0 && (
              <p className="text-xs text-sky-600 mb-2 text-center">Belanja dicatat sebagai utang — dibayar saat gajian</p>
            )}
            <Button onClick={checkout} disabled={processing || !buyerReady || cart.length === 0 || insufficient} data-testid="pos-checkout-button" className="w-full py-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-base font-semibold">
              {processing ? "Memproses…" : mode === "santri" ? "Bayar dengan Uang Jajan" : mode === "asc" ? "Bayar dengan Kartu ASC" : "Catat Belanja Pendidik"}
            </Button>
          </div>
        </div>
      </div>

      <QrScanner
        open={scanFor === "student"}
        onOpenChange={(o) => !o && setScanFor(null)}
        onResult={(t) => { setScanFor(null); doLookup("code", t); }}
        title="Pindai QR Santri"
      />
      <QrScanner
        open={scanFor === "educator"}
        onOpenChange={(o) => !o && setScanFor(null)}
        onResult={(t) => { setScanFor(null); lookupEducator(t); }}
        title="Pindai QR Pendidik"
      />
      <QrScanner
        open={scanFor === "asc"}
        onOpenChange={(o) => !o && setScanFor(null)}
        onResult={(t) => { setScanFor(null); lookupAsc(t); }}
        title="Pindai QR Kartu ASC"
      />
      <QrScanner
        open={scanFor === "product"}
        onOpenChange={(o) => !o && setScanFor(null)}
        onResult={onScanProduct}
        title="Pindai Barcode Barang"
      />
    </div>
  );
}

const BuyerPhoto = ({ path }) => (
  <div className="w-14 h-14 rounded-xl overflow-hidden bg-white border border-stone-200 flex items-center justify-center shrink-0">
    {path ? <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" /> : <User size={22} className="text-stone-300" />}
  </div>
);
